import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Quote, ChevronLeft, ChevronRight, Star } from "lucide-react";
import { TESTIMONIALS } from "@/lib/media";

export default function Testimonials() {
  const [idx, setIdx] = useState(0);
  const next = () => setIdx((i) => (i + 1) % TESTIMONIALS.length);
  const prev = () => setIdx((i) => (i - 1 + TESTIMONIALS.length) % TESTIMONIALS.length);

  useEffect(() => {
    const t = setInterval(next, 7000);
    return () => clearInterval(t);
  }, []);

  const t = TESTIMONIALS[idx];

  return (
    <section className="bg-white">
      <div className="max-w-7xl mx-auto px-6 md:px-10 py-20 md:py-28">
        <div className="grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-5">
            <div className="section-eyebrow mb-5">Client Voices</div>
            <h2 className="font-display font-semibold text-3xl sm:text-4xl lg:text-5xl tracking-tight text-[#0A2540] leading-tight">
              The work speaks. Our clients say it better.
            </h2>
            <p className="mt-6 text-slate-600 leading-relaxed max-w-md">
              Operating leaders from manufacturing, healthcare, logistics and beyond on what changed after working with Clinivon.
            </p>

            <div className="mt-10 flex items-center gap-2">
              <button onClick={prev} data-testid="testimonials-prev" className="h-10 w-10 grid place-items-center bg-white border border-slate-300 hover:border-[#0A2540] transition-colors" aria-label="Previous testimonial">
                <ChevronLeft size={16} />
              </button>
              <button onClick={next} data-testid="testimonials-next" className="h-10 w-10 grid place-items-center bg-[#0A2540] text-white hover:bg-[#061A30] transition-colors" aria-label="Next testimonial">
                <ChevronRight size={16} />
              </button>
              <div className="ml-3 text-xs text-slate-500 tabular">
                <span className="text-[#0A2540] font-semibold">{String(idx + 1).padStart(2, "0")}</span>
                <span className="text-slate-300 mx-1.5">/</span>
                <span>{String(TESTIMONIALS.length).padStart(2, "0")}</span>
              </div>
            </div>
          </div>

          <div className="lg:col-span-7">
            <div className="relative bg-[#F8FAFC] border border-slate-200 p-8 md:p-12 min-h-[320px] flex items-center">
              <Quote size={56} className="absolute top-6 right-8 text-[#10B981]/15" strokeWidth={1.5} />

              <AnimatePresence mode="wait">
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -8 }}
                  transition={{ duration: 0.45 }}
                  className="relative w-full"
                >
                  <div className="flex items-center gap-0.5 mb-5 text-[#10B981]">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <Star key={i} size={14} fill="currentColor" />
                    ))}
                  </div>
                  <p className="font-display text-lg md:text-xl text-[#0A2540] leading-relaxed">
                    &ldquo;{t.quote}&rdquo;
                  </p>
                  <div className="mt-7 flex items-center gap-4 border-t border-slate-200 pt-5">
                    <img
                      src={t.avatar}
                      alt={t.name}
                      loading="lazy"
                      className="h-12 w-12 rounded-full object-cover border-2 border-white shadow-sm"
                    />
                    <div>
                      <div className="font-display font-semibold text-[#0A2540]">{t.name}</div>
                      <div className="text-xs text-slate-500">{t.title}</div>
                    </div>
                  </div>
                </motion.div>
              </AnimatePresence>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
